import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RefreshCw, Copy, Check, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { TopNavigation } from '@/components/layout';
import { TraceSidebarProvider, useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { TraceSidebar } from '@/components/trace-sidebar';
import { TraceDetailPanel } from '@/components/trace-detail';
import { fetchTrajectory } from '@/services/api';
import { fetchTrajectoryHITLRequests, type HITLRequest } from '@/services/hitlApi';
import { transformToTraceItems } from '@/utils/traceTransformer';
import MermaidDiagram from '@/components/MermaidDiagram.jsx';
import LogViewer from '@/components/LogViewer';
import { HITLResponsePanel } from '@/components/HITLResponsePanel';
import type { TrajectoryData, TraceItem } from '@/types/trace';

function TrajectoryViewContent() {
  const { trajectoryId } = useParams<{ trajectoryId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TrajectoryData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(400); // Default wider for waterfall
  const [isResizing, setIsResizing] = useState(false);
  const [hitlRequests, setHitlRequests] = useState<HITLRequest[]>([]);

  // Get pending HITL request (if any)
  const pendingHitlRequest = useMemo(() => {
    return hitlRequests.find(
      (r) => r.status === 'pending' && new Date(r.expires_at) > new Date()
    );
  }, [hitlRequests]);

  // Fetch HITL requests for this trajectory
  const loadHitlRequests = async () => {
    if (!trajectoryId) return;
    try {
      const requests = await fetchTrajectoryHITLRequests(trajectoryId);
      setHitlRequests(requests);
    } catch (err) {
      console.error('Failed to load HITL requests:', err);
    }
  };

  // Handle sidebar resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.max(280, Math.min(800, e.clientX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const copyTrajectoryId = async () => {
    if (!trajectoryId) return;
    try {
      await navigator.clipboard.writeText(trajectoryId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const { state, selectTrace, setDetailPanelOpen } = useTraceSidebar();

  // Track if we're programmatically updating to avoid race condition
  const isUpdatingFromState = useRef(false);

  // Transform data to trace items
  const traceItems = useMemo<TraceItem[]>(() => {
    if (!data) return [];
    return transformToTraceItems(data);
  }, [data]);

  // Sync URL -> State (only when URL changes externally, e.g., browser back/forward)
  useEffect(() => {
    // Skip if we're the ones who updated the URL
    if (isUpdatingFromState.current) {
      isUpdatingFromState.current = false;
      return;
    }

    const traceId = searchParams.get('trace');
    if (traceId && traceId !== state.selectedTraceId) {
      selectTrace(traceId);
    }
  }, [searchParams, selectTrace]);

  // Sync State -> URL (when selection changes via clicks)
  useEffect(() => {
    const currentUrlTrace = searchParams.get('trace');

    // Only update URL if state changed to something different than current URL
    if (state.selectedTraceId && state.selectedTraceId !== currentUrlTrace) {
      isUpdatingFromState.current = true;
      setSearchParams({ trace: state.selectedTraceId });
    } else if (!state.selectedTraceId && currentUrlTrace) {
      isUpdatingFromState.current = true;
      setSearchParams({});
    }
  }, [state.selectedTraceId, setSearchParams, searchParams]);

  // Fetch trajectory data
  const loadTrajectory = async () => {
    if (!trajectoryId) return;

    try {
      setError(null);
      if (!data) setLoading(true);
      else setRefreshing(true);

      const result = await fetchTrajectory(trajectoryId);
      setData(result);
    } catch (err) {
      console.error('Failed to load trajectory:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trajectory');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTrajectory();
    loadHitlRequests();
  }, [trajectoryId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading trajectory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background p-8">
        <Alert variant="destructive" className="max-w-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading trajectory</AlertTitle>
          <AlertDescription className="mt-2">
            {error}
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/trajectories')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to list
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const statusVariant = {
    completed: 'default',
    running: 'secondary',
    failed: 'destructive',
    cancelled: 'outline',
  }[data.status] as 'default' | 'secondary' | 'destructive' | 'outline';

  return (
    <>
      <TopNavigation />
      <div className="flex flex-col h-[calc(100vh-3.5rem)] bg-background">
        {/* Trajectory Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b bg-slate-800 dark:bg-slate-900 text-white">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/90 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/trajectories')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold truncate max-w-md">
                  {data.report_title || 'Trajectory'}
                </h1>
                <Badge
                  variant="outline"
                  className="border-white/30 text-white bg-white/10"
                >
                  {data.status}
                </Badge>
              </div>
              {data.type && (
                <p className="text-xs text-white/60 mt-0.5">
                  {data.type === 'agent' ? 'Agent Run' : data.type}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={copyTrajectoryId}
              className="flex items-center gap-2 text-sm text-white/60 font-mono hover:text-white/90 transition-colors cursor-pointer"
              title="Click to copy full UUID"
            >
              <span>{trajectoryId}</span>
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white/90 hover:text-white hover:bg-white/10"
              onClick={loadTrajectory}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>

        {/* Date Awareness Warnings Banner */}
        {data.date_override && data.date_awareness_warnings && data.date_awareness_warnings.length > 0 && (
          <div className="px-4 py-2 space-y-2 bg-muted/50 border-b">
            {/* Warning severity: known non-date-aware tools */}
            {data.date_awareness_warnings.filter((w: { severity: string }) => w.severity === 'warning').length > 0 && (
              <Alert variant="destructive" className="py-2">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="text-sm font-medium">Non-Date-Aware Tools Used</AlertTitle>
                <AlertDescription className="text-sm">
                  This run used date override ({data.date_override}) but called tools that don't support date parameters:
                  <span className="ml-1 font-mono">
                    {data.date_awareness_warnings
                      .filter((w: { severity: string }) => w.severity === 'warning')
                      .map((w: { tool_name: string }) => w.tool_name)
                      .join(', ')}
                  </span>
                </AlertDescription>
              </Alert>
            )}
            {/* Info severity: unknown tools (assumed safe) */}
            {data.date_awareness_warnings.filter((w: { severity: string }) => w.severity === 'info').length > 0 && (
              <Alert className="py-2 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Unknown Tool Date Support
                </AlertTitle>
                <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
                  {data.date_awareness_warnings.filter((w: { severity: string }) => w.severity === 'info').length} tool(s) have no date override metadata (assumed safe)
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* HITL Response Panel */}
        {pendingHitlRequest && (
          <div className="px-4 py-3 border-b">
            <HITLResponsePanel
              hitlRequest={pendingHitlRequest}
              onResponse={() => {
                loadHitlRequests();
                loadTrajectory();
              }}
            />
          </div>
        )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - resizable */}
        <aside
          className="flex-shrink-0 border-r relative"
          style={{ width: sidebarWidth }}
        >
          <TraceSidebar items={traceItems} trajectoryId={trajectoryId} />
          {/* Resize handle */}
          <div
            className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/50 transition-colors ${isResizing ? 'bg-primary' : 'bg-transparent'}`}
            onMouseDown={handleMouseDown}
          />
        </aside>

        {/* Main Content Area - Detail Panel takes priority */}
        <main className="flex-1 overflow-hidden">
          {state.detailPanelOpen ? (
            <TraceDetailPanel items={traceItems} />
          ) : (
            <div className="h-full overflow-auto p-6">
              {/* Trajectory Overview - shown when no item selected */}
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl font-semibold mb-4">Trajectory Overview</h2>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="text-sm text-muted-foreground">Status</div>
                    <div className="font-medium mt-1">
                      <Badge variant={statusVariant}>{data.status}</Badge>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="text-sm text-muted-foreground">Steps</div>
                    <div className="font-medium mt-1">{traceItems.length}</div>
                  </div>
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="text-sm text-muted-foreground">Timestamp</div>
                    <div className="font-medium mt-1 text-sm">
                      {data.timestamp ? new Date(data.timestamp).toLocaleString() : '-'}
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="text-sm text-muted-foreground">ID</div>
                    <div className="font-mono text-xs mt-1 truncate">{trajectoryId}</div>
                  </div>
                </div>

                {/* Execution Flow Graph */}
                {(() => {
                  const mermaidDiagrams = data.mermaid_diagrams as Record<string, unknown> | undefined;
                  const executionPath = mermaidDiagrams?.execution_path as string | undefined;
                  const taskPlans = mermaidDiagrams?.task_plans as Record<string, { task_plan_mermaid?: string }> | undefined;
                  const taskPlanMermaid = taskPlans
                    ? Object.values(taskPlans).find(p => p?.task_plan_mermaid)?.task_plan_mermaid
                    : undefined;
                  const diagramToShow = taskPlanMermaid || executionPath;

                  return diagramToShow ? (
                    <div className="mb-8">
                      <MermaidDiagram
                        diagram={diagramToShow}
                        title="Graph Execution Flow"
                        description="This diagram shows the task plan created by the orchestrator, including task dependencies and execution order."
                      />
                    </div>
                  ) : null;
                })()}

                {/* Run Logs */}
                {trajectoryId && <LogViewer trajectoryId={trajectoryId} />}

                <div className="text-center text-muted-foreground py-8 border rounded-lg bg-muted/30">
                  <p className="font-medium">Select a trace item from the sidebar</p>
                  <p className="text-sm mt-2">
                    Click on any item in the tree to view its details
                  </p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      </div>
    </>
  );
}

export function TrajectoryView() {
  const [searchParams] = useSearchParams();
  const initialTraceId = searchParams.get('trace');

  return (
    <TraceSidebarProvider initialSelectedId={initialTraceId}>
      <TrajectoryViewContent />
    </TraceSidebarProvider>
  );
}

export default TrajectoryView;
